import { assetUrl } from '../api/client';
import Reveal from './Reveal';
import HeartDivider from './HeartDivider';

/**
 * Premium shared page header.
 *
 * Two treatments share one set of props:
 *  - variant="split" (default when an `image` is provided): an asymmetric
 *    two-column layout — headline on one side, an arched photo with the
 *    signature maroon shape + gold ring accent on the other.
 *  - variant="overlay": a full-bleed photo with a maroon `.hero-overlay`.
 *  - no `image`: a clean drenched-maroon band with a subtle gold glow.
 *
 * Props: { eyebrow?, title, subtitle?, image?, variant?, align?, caption?, children }
 *  - caption: { name, role? } — names the person in the split-layout photo
 *    (a small card anchored to the bottom-left of the arch, styled like the
 *    president block on club cards). Ignored by the other variants.
 */
export default function PageHero({
  eyebrow,
  title,
  subtitle,
  image,
  variant,
  align = 'left',
  caption,
  children,
}) {
  // Public root-relative paths (`/img/...`) and absolute URLs must pass
  // through untouched; only backend-stored relative paths go via assetUrl.
  const src = image
    ? (/^(https?:\/\/|\/)/.test(image) ? image : assetUrl(image))
    : null;
  const mode = variant || (src ? 'split' : 'plain');

  // Full-bleed photo with overlay.
  if (mode === 'overlay' && src) {
    return (
      <header className="relative overflow-hidden">
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-overlay" />
        <Reveal className={`container-page relative py-20 sm:py-28 ${align === 'center' ? 'text-center' : ''}`}>
          {eyebrow && <p className="eyebrow mb-3 text-gold-400">{eyebrow}</p>}
          <h1 className="font-serif text-4xl font-bold tracking-normal text-white sm:text-5xl">
            {title}
          </h1>
          <HeartDivider className={`mt-5 text-gold-400 ${align === 'center' ? 'justify-center' : ''}`} />
          {subtitle && (
            <p className={`mt-4 text-lg text-cream-200 ${align === 'center' ? 'mx-auto' : ''} max-w-2xl`}>
              {subtitle}
            </p>
          )}
          {children}
        </Reveal>
      </header>
    );
  }

  // Asymmetric split with arched photo + signature device.
  if (mode === 'split' && src) {
    return (
      <header className="relative overflow-hidden bg-cream-100">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 -top-40 hidden h-[36rem] w-[36rem] rounded-full bg-maroon-50/70 blur-2xl lg:block"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-gold-100/50 blur-2xl"
        />
        <div className="container-page relative grid items-center gap-12 py-16 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:py-24">
          <Reveal>
            {eyebrow && <p className="eyebrow mb-4 text-maroon-700">{eyebrow}</p>}
            <h1 className="heading-brand text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
              {title}
            </h1>
            <HeartDivider className="mt-5" />
            {subtitle && (
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft">{subtitle}</p>
            )}
            {children}
          </Reveal>

          <Reveal delay={150} className="relative mx-auto w-full max-w-md lg:mx-0">
            <div
              aria-hidden="true"
              className="absolute -left-5 -top-5 h-full w-full rounded-t-[8rem] rounded-b-2xl bg-maroon-700/90"
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-5 -right-5 h-28 w-28 rounded-full border-[6px] border-gold-400"
            />
            <div className="arch relative shadow-lift">
              <img
                src={src}
                alt={caption?.name || ''}
                aria-hidden={caption ? undefined : 'true'}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            {caption?.name && (
              <div className="absolute -bottom-4 left-4 z-10 max-w-[78%] rounded-xl bg-white/95 px-4 py-3 shadow-lift ring-1 ring-maroon-100 backdrop-blur">
                <p className="font-display font-semibold leading-tight text-ink">{caption.name}</p>
                {caption.role && (
                  <p className="mt-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-maroon-700">
                    {caption.role}
                  </p>
                )}
              </div>
            )}
          </Reveal>
        </div>
      </header>
    );
  }

  // Plain drenched-maroon band (no image).
  return (
    <header className="relative overflow-hidden bg-gradient-to-br from-maroon-800 to-maroon-950">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gold-400/10 blur-2xl"
      />
      <Reveal className={`container-page relative py-20 sm:py-28 ${align === 'center' ? 'text-center' : ''}`}>
        {eyebrow && <p className="eyebrow mb-3 text-gold-400">{eyebrow}</p>}
        <h1 className="font-serif text-4xl font-bold tracking-normal text-white sm:text-5xl">
          {title}
        </h1>
        <HeartDivider className={`mt-5 text-gold-400 ${align === 'center' ? 'justify-center' : ''}`} />
        {subtitle && (
          <p className={`mt-4 text-lg text-cream-200 ${align === 'center' ? 'mx-auto' : ''} max-w-2xl`}>
            {subtitle}
          </p>
        )}
        {children}
      </Reveal>
    </header>
  );
}
